// ─────────────────────────────────────────────
//  Footer.jsx  —  Footer UI
//  White background · black text · matches Navbar logo
// ─────────────────────────────────────────────

import React, { useState, useEffect, useCallback } from "react";
import "./Footer.css";
import { Link } from "react-router-dom";
import logoImg from "../../../assets/PNG/Logo.png";
import logoGifImg from "../../../assets/Gif/Logo.gif";
import { resolveIcon } from "./FooterData.js";
import { fetchContent } from "../../../services/contentService";

function ChevronDown() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

export default function Footer() {
  const [dynamicContent, setDynamicContent] = useState(null);
  const [openSections, setOpenSections] = useState([]);
  const [isCompact, setIsCompact] = useState(false);

  useEffect(() => {
    const checkWidth = () => setIsCompact(window.innerWidth < 1025);
    checkWidth();
    window.addEventListener("resize", checkWidth);
    return () => window.removeEventListener("resize", checkWidth);
  }, []);

  const toggleSection = useCallback((idx) => {
    setOpenSections((prev) =>
      prev.includes(idx) ? prev.filter((i) => i !== idx) : [...prev, idx]
    );
  }, []);

  useEffect(() => {
    const load = () =>
      fetchContent("footer").then((section) => {
        if (section && section.content) {
          setDynamicContent(section.content);
        }
      });
    load();
    const interval = setInterval(load, 30000);
    return () => clearInterval(interval);
  }, []);

  const content = dynamicContent || {};

  const socials = content.socials || [];
  const brandQuoteText = content.brand?.quote || "";
  const logoImageVal = content.brand?.logoImage || logoImg;
  const logoGifVal = content.brand?.logoGif || logoGifImg;
  const logoMainTextVal = content.brand?.logoMainText || "";
  const logoSubTextVal = content.brand?.logoSubText || "";
  const headingSections = content.headingSections || [];
  const copyrightTextVal = content.brand?.copyright || "";
  const bottomTaglineTextVal = content.brand?.tagline || "";

  return (
    <footer className="ft">
      <div className="ft__inner">

        {/* ═══════════════════════════════════
            TOP GRID — 2 columns: left (brand) + right (links)
        ═══════════════════════════════════ */}
        <div className="ft__grid">

          {/* ── Left: Brand column ── */}
          <div className="ft__col ft__col--brand">
            <Link to="/" className="ft__logo">
              <div className="ft__logo-img-wrap">
                <img src={logoImageVal} alt="TheVisaCo Logo" className="ft__logo-img" />
                <img src={logoGifVal} alt="TheVisaCo" className="ft__logo-gif" />
              </div>
              <div className="ft__logo-words">
                <span style={{ fontFamily: "'PT Serif', serif" }} className="ft__logo-main">{logoMainTextVal}</span>
                <span style={{
                  fontFamily: "'PT Serif', serif",
                  color: "#06263e"
                }} className="ft__logo-sub">{logoSubTextVal}</span>
              </div>
            </Link>

            <blockquote className="ft__quote">
              <span className="ft__quote-mark">"</span>
              {brandQuoteText}
              <span className="ft__quote-mark">"</span>
            </blockquote>

            <div className="ft__socials">
              {socials.map((s, idx) => (
                <a
                  key={s.label || idx}
                  href={s.href}
                  className="ft__social"
                  aria-label={s.label}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {resolveIcon(s.icon)}
                </a>
              ))}
            </div>
          </div>

          {/* ── Right: Links columns ── */}
          <div className="ft__col ft__col--right">
            <div className="ft__right-grid">
                  {headingSections.length > 0 ? (
                headingSections.map((section, sIdx) => {
                  const isOffice = section.sectionType === "office";
                  const isOpen = openSections.includes(sIdx);
                  return (
                  <div key={sIdx} className="ft__col">
                    {isCompact ? (
                      <button
                        className={`ft__heading ft__heading--btn${isOpen ? " ft__heading--open" : ""}`}
                        onClick={() => toggleSection(sIdx)}
                        aria-expanded={isOpen}
                      >
                        {section.heading}
                        <span className="ft__chevron"><ChevronDown /></span>
                      </button>
                    ) : (
                      <h6 className="ft__heading">{section.heading}</h6>
                    )}
                    <div className={`ft__list-wrap${isCompact ? (isOpen ? " ft__list-wrap--open" : "") : " ft__list-wrap--always"}`}>
                      {isOffice ? (
                        <ul className="ft__list ft__list--office">
                          {(section.rows || []).map((row, rIdx) => (
                            <li key={rIdx} className="ft__office-item">
                              <span className="ft__office-icon">{resolveIcon(row.icon)}</span>
                              <span style={{ color: "var(--ft-muted)" }}>{row.text}</span>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <ul className="ft__list">
                          {(section.rows || []).map((row, rIdx) => (
                            <li key={rIdx}>
                              <Link to={row.href || "#"} className="ft__link">{row.label}</Link>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                  );
                })
              ) : null}
            </div>
          </div>

        </div>

        {/* ═══════════════════════════════════
            BOTTOM BAR
        ═══════════════════════════════════ */}
        <div className="ft__bottom">
          <span>{copyrightTextVal}</span>
          <span className="ft__bottom-tagline">{bottomTaglineTextVal}</span>
        </div>

      </div>
    </footer>
  );
}